import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Badge, statusTone, Money, EmptyState } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { BookOpen, CheckCircle2, XCircle, Eye } from "lucide-react";
import { toast } from "sonner";

export default function AdminCourses() {
  const { can } = useAuth();
  const [courses, setCourses] = useState([]);
  const [tab, setTab] = useState("submitted");
  const [remarkFor, setRemarkFor] = useState(null);
  const [remark, setRemark] = useState("");

  const load = useCallback(() => {
    const status = tab === "all" ? "" : tab;
    api.get(`/courses?status=${status}`).then(({ data }) => setCourses(data));
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  const act = async (course, status, rmk = "") => {
    await api.post(`/courses/${course.id}/status`, { status, remark: rmk });
    toast.success(`Course ${status}`);
    setRemarkFor(null); setRemark(""); load();
  };

  return (
    <div data-testid="admin-courses">
      <PageHeader title="Course Approvals" subtitle="Review, approve and publish courses" />
      <Tabs value={tab} onValueChange={setTab} className="mb-6">
        <TabsList>
          <TabsTrigger value="submitted" data-testid="tab-submitted">Submitted</TabsTrigger>
          <TabsTrigger value="under_review">Under Review</TabsTrigger>
          <TabsTrigger value="approved">Approved</TabsTrigger>
          <TabsTrigger value="published">Published</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      {courses.length === 0 ? <EmptyState icon={BookOpen} title="No courses in this state" /> : (
        <div className="space-y-3">
          {courses.map((c) => (
            <div key={c.id} className="bg-white border border-border rounded-lg p-4 flex flex-wrap items-center gap-4" data-testid={`admin-course-${c.id}`}>
              <img src={c.thumbnail} alt="" className="h-16 w-24 object-cover rounded" />
              <div className="flex-1 min-w-[180px]">
                <h3 className="font-semibold text-navy">{c.title}</h3>
                <p className="text-sm text-muted-foreground">{c.instructor_name} • <Money amount={c.discount_price || c.price} /></p>
              </div>
              <Badge tone={statusTone(c.status)}>{c.status}</Badge>
              <div className="flex gap-2 flex-wrap">
                <a href={`/courses/${c.id}`} target="_blank" rel="noreferrer"><Button size="sm" variant="outline"><Eye size={14} className="mr-1" /> Preview</Button></a>
                {can("courses", "approve") && ["submitted", "under_review"].includes(c.status) && (
                  <>
                    {c.status === "submitted" && <Button size="sm" variant="outline" onClick={() => act(c, "under_review")}>Start Review</Button>}
                    <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" onClick={() => act(c, "approved")} data-testid={`approve-${c.id}`}><CheckCircle2 size={14} className="mr-1" /> Approve</Button>
                    <Button size="sm" className="bg-crimson text-white hover:bg-crimson/90" onClick={() => { setRemarkFor(c); }} data-testid={`reject-${c.id}`}><XCircle size={14} className="mr-1" /> Reject</Button>
                  </>
                )}
                {can("courses", "publish") && c.status === "approved" && <Button size="sm" className="viriks-navy text-white" onClick={() => act(c, "published")} data-testid={`publish-${c.id}`}>Publish</Button>}
                {can("courses", "publish") && c.status === "published" && <Button size="sm" variant="outline" onClick={() => act(c, "unpublished")}>Unpublish</Button>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!remarkFor} onOpenChange={(o) => !o && setRemarkFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Reject Course</DialogTitle></DialogHeader>
          <Textarea placeholder="Reason for rejection / requested changes..." value={remark} onChange={(e) => setRemark(e.target.value)} data-testid="reject-remark" />
          <DialogFooter><Button className="bg-crimson text-white" onClick={() => act(remarkFor, "rejected", remark)} data-testid="confirm-reject">Reject with Remark</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
